import { z } from 'zod';
import { DEFAULT_CURRENCY, MALAYSIAN_STATE_CODES, PAGINATION, RESERVED_MERCHANT_SLUGS, SUPPORTED_CURRENCIES, } from '../constants/index.js';
/** Reusable primitives shared by every request schema. */
export const uuidSchema = z.string().uuid('Must be a valid identifier');
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(254)
    .email('Enter a valid email address');
/**
 * A money amount on the wire is always a string with at most 2 decimals.
 * Numbers are rejected outright so float values can never enter the system.
 */
export const moneySchema = z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount, e.g. 49.90');
/**
 * An amount that may be left out. A blank or whitespace-only input means "none"
 * (null) rather than an invalid amount — forms hint "leave blank for none" and a
 * cleared text box is sent as an empty string, which the amount check alone
 * would reject as "Enter a valid amount".
 */
export const optionalMoneySchema = z
    .union([
    z
        .string()
        .trim()
        .max(0)
        .transform(() => null),
    moneySchema,
])
    .nullable()
    .optional();
/** URL-safe slug: lowercase letters, digits and single hyphens. */
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3, 'Must be at least 3 characters')
    .max(63, 'Must be 63 characters or fewer')
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and hyphens only');
export const merchantSlugSchema = slugSchema.refine((value) => !RESERVED_MERCHANT_SLUGS.includes(value), { message: 'This store address is reserved. Please choose another.' });
/** Malaysian mobile/landline in local or +60 form. */
export const phoneSchema = z
    .string()
    .trim()
    .regex(/^(\+?60|0)[1-9]\d{7,9}$/, 'Enter a valid Malaysian phone number, e.g. 0123456789');
export const optionalPhoneSchema = phoneSchema.or(z.literal('')).nullable().optional();
export const postcodeSchema = z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'Enter a valid 5-digit postcode');
export const malaysianStateSchema = z
    .string()
    .trim()
    .toUpperCase()
    .refine((value) => MALAYSIAN_STATE_CODES.includes(value), {
    message: 'Select a valid Malaysian state',
});
export const currencySchema = z
    .enum(SUPPORTED_CURRENCIES)
    .default(DEFAULT_CURRENCY);
/**
 * Password policy: length does the heavy lifting, plus a light character-class
 * requirement. Deliberately not so strict that users resort to reuse.
 */
export const passwordSchema = z
    .string()
    .min(10, 'Use at least 10 characters')
    .max(128, 'Use 128 characters or fewer')
    .regex(/[a-z]/, 'Include at least one lowercase letter')
    .regex(/[A-Z]/, 'Include at least one uppercase letter')
    .regex(/\d/, 'Include at least one number');
export const addressSchema = z.object({
    line1: z.string().trim().min(1, 'Address is required').max(200),
    line2: z.string().trim().max(200).nullable().optional(),
    city: z.string().trim().min(1, 'City is required').max(100),
    state: malaysianStateSchema,
    postcode: postcodeSchema,
    country: z.string().trim().length(2).toUpperCase().default('MY'),
});
/** Standard list-endpoint query string. */
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(PAGINATION.DEFAULT_PAGE),
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(PAGINATION.MAX_PAGE_SIZE)
        .default(PAGINATION.DEFAULT_PAGE_SIZE),
});
export const sortOrderSchema = z.enum(['asc', 'desc']).default('desc');
export const searchQuerySchema = z.object({
    search: z.string().trim().max(200).optional(),
});
/** ISO date-time string coerced to a Date. */
export const dateSchema = z.coerce.date();
export const dateRangeQuerySchema = z
    .object({
    from: dateSchema.optional(),
    to: dateSchema.optional(),
})
    .refine((v) => !v.from || !v.to || v.from <= v.to, {
    message: 'The start date must be on or before the end date',
    path: ['from'],
});
/** Named ranges offered by the dashboard date filter. */
export const reportPeriodSchema = z.enum([
    'today',
    'yesterday',
    'this_week',
    'this_month',
    'last_month',
    'custom',
]);
/** Turn arbitrary text into a candidate slug. */
export function slugify(input) {
    return input
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 63);
}
//# sourceMappingURL=common.js.map